import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Loader2, KeyRound, ShieldAlert } from "lucide-react";
import {
  decodeRequestOptions, encodeAssertion, webauthnSupported,
} from "@/lib/webauthn";

const AUTH_BG = "https://images.unsplash.com/photo-1497366754035-f200968a6e72?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBvZmZpY2UlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzgxMzIwMjAyfDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login, verify2fa, finishYubikey, yubikeyFallback } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [twofa, setTwofa] = useState(null);  // { challenge_id, phone_hint }
  const [code, setCode] = useState("");
  const [yubikey, setYubikey] = useState(null);  // { challenge_id, publicKey }
  const [showFallback, setShowFallback] = useState(false);
  const [fallbackPwd, setFallbackPwd] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await login(email, password);
      if (res?.requires_2fa) {
        setTwofa({
          challenge_id: res.challenge_id,
          phone_hint: res.phone_hint,
          email_hint: res.email_hint,
          sms_sent: res.sms_sent,
          email_sent: res.email_sent,
          debug_code: res.debug_code,
        });
        const channels = [
          res.sms_sent ? "phone" : null,
          res.email_sent ? "email" : null,
        ].filter(Boolean).join(" + ");
        toast.info(channels
          ? `Verification code sent to your ${channels}`
          : "Verification code generated — check your phone & email");
        return;
      }
      toast.success(`Welcome, ${res.name}`);
      navigate(res.role === "admin" ? "/dashboard" : "/portal", { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid credentials");
    } finally {
      setBusy(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!code || code.length < 6) {
      toast.error("Enter the 6-digit code");
      return;
    }
    setBusy(true);
    try {
      const res = await verify2fa(twofa.challenge_id, code);
      if (res?.requires_yubikey) {
        setTwofa(null); setCode("");
        setYubikey({
          challenge_id: res.yubikey_challenge_id,
          publicKey: res.publicKey,
        });
        // Auto-trigger the browser WebAuthn prompt right away
        setTimeout(() => attemptYubikey(res.yubikey_challenge_id, res.publicKey), 250);
        return;
      }
      toast.success(`Welcome, ${res.name}`);
      navigate(res.role === "admin" ? "/dashboard" : "/portal", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verification failed");
    } finally { setBusy(false); }
  };

  const attemptYubikey = async (challengeId, publicKey) => {
    if (!webauthnSupported()) {
      toast.error("This browser doesn't support WebAuthn — use the master fallback password.");
      setShowFallback(true);
      return;
    }
    setBusy(true);
    try {
      const opts = decodeRequestOptions(publicKey);
      const credential = await navigator.credentials.get({ publicKey: opts });
      if (!credential) {
        toast.error("YubiKey prompt cancelled");
        setBusy(false);
        return;
      }
      const payload = encodeAssertion(credential);
      const u = await finishYubikey(challengeId, payload);
      toast.success(`Welcome, ${u.name}`);
      navigate(u.role === "admin" ? "/dashboard" : "/portal", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail
        || err.message
        || "YubiKey verification failed — you can use the master fallback password.");
      setShowFallback(true);
    } finally { setBusy(false); }
  };

  const handleFallback = async (e) => {
    e.preventDefault();
    if (!fallbackPwd) return;
    setBusy(true);
    try {
      const u = await yubikeyFallback(yubikey.challenge_id, fallbackPwd);
      toast.success(`Welcome, ${u.name}`);
      navigate(u.role === "admin" ? "/dashboard" : "/portal", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fallback failed");
    } finally { setBusy(false); }
  };

  const resetLogin = () => {
    setTwofa(null); setCode("");
    setYubikey(null); setShowFallback(false); setFallbackPwd("");
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-5">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:col-span-3 relative overflow-hidden bg-zinc-900">
        <img src={AUTH_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-[#002FA7] flex items-center justify-center overflow-hidden">
              <img src="/iceberg-logo.png" alt="Iceberg Digital" className="h-7 w-7 object-contain" />
            </div>
            <span className="font-display font-bold text-xl tracking-tight">Iceberg Digital</span>
          </div>
          <div className="space-y-4 max-w-xl">
            <div className="label-overline !text-white">Digital Intelligence</div>
            <h1 className="font-display font-extrabold text-5xl xl:text-6xl leading-none tracking-tight">
              Organisational Integrations.<br/><span className="text-[#FFC107]">One ICT Provider.</span>
            </h1>
            <p className="text-white/80 text-base max-w-md leading-relaxed">
              Understand your organisations digital landscape, how it effects you, and where you
              can gain meaningful growth in your organisation with transparent and manageable
              protection.
            </p>
            <div className="pt-2 space-y-1.5">
              <div className="text-white/60 text-[11px] uppercase tracking-widest font-semibold">
                ICT — Information &amp; Communication Technology
              </div>
              <ul className="grid grid-cols-2 gap-x-4 gap-y-1 text-white/80 text-[13px] max-w-md">
                <li className="flex items-center gap-2"><span className="text-[#FFC107]">•</span> IT Network Management</li>
                <li className="flex items-center gap-2"><span className="text-[#FFC107]">•</span> API Integrations</li>
                <li className="flex items-center gap-2"><span className="text-[#FFC107]">•</span> AI Integrations</li>
                <li className="flex items-center gap-2"><span className="text-[#FFC107]">•</span> Software Development</li>
                <li className="flex items-center gap-2"><span className="text-[#FFC107]">•</span> Data Management</li>
                <li className="flex items-center gap-2"><span className="text-[#FFC107]">•</span> Digital Footprint</li>
                <li className="flex items-center gap-2"><span className="text-[#FFC107]">•</span> Digital Communication</li>
                <li className="flex items-center gap-2"><span className="text-[#FFC107]">•</span> Digital Exposure</li>
                <li className="flex items-center gap-2"><span className="text-[#FFC107]">•</span> Digital Phone Systems</li>
                <li className="flex items-center gap-2"><span className="text-[#FFC107]">•</span> Disaster Recovery</li>
              </ul>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-3 text-white/70 text-xs">
              <Link to="/terms" className="hover:text-white" data-testid="login-left-terms-link">
                Terms of Service
              </Link>
              <span className="text-white/30">·</span>
              <Link to="/privacy" className="hover:text-white" data-testid="login-left-privacy-link">
                Privacy Policy
              </Link>
            </div>
            <div className="text-white/50 text-xs label-overline">© Iceberg Digital {new Date().getFullYear()}</div>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="lg:col-span-2 flex items-center justify-center p-6 sm:p-12 bg-white">
        <div className="w-full max-w-sm space-y-8">
          <div>
            <div className="label-overline mb-3">Sign in</div>
            <h2 className="font-display font-extrabold text-3xl tracking-tight">Welcome back.</h2>
            <p className="text-sm text-zinc-500 mt-2">Admins use email · Clients can use Google.</p>
          </div>

          <Tabs defaultValue="email" data-testid="login-tabs">
            <TabsList className="grid grid-cols-2 w-full rounded-sm">
              <TabsTrigger value="email" data-testid="tab-email">Email</TabsTrigger>
              <TabsTrigger value="google" data-testid="tab-google">Google</TabsTrigger>
            </TabsList>

            <TabsContent value="email" className="mt-6">
              {yubikey ? (
                <div className="space-y-4" data-testid="login-yubikey-step">
                  <div>
                    <div className="label-overline mb-1 flex items-center gap-1.5">
                      <KeyRound className="h-3.5 w-3.5" /> Hardware security key
                    </div>
                    <p className="text-sm text-zinc-500">
                      Touch your registered <strong>YubiKey</strong> to finish signing in.
                      If your key is unavailable, use the master fallback password.
                    </p>
                  </div>
                  {!showFallback ? (
                    <>
                      <div className="rounded-sm border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900 flex items-center gap-2">
                        <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-700" />
                        Waiting for touch on your YubiKey…
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button type="button" disabled={busy}
                          onClick={() => attemptYubikey(yubikey.challenge_id, yubikey.publicKey)}
                          className="h-10 rounded-sm bg-[#002FA7] hover:bg-[#00227A]"
                          data-testid="login-yubikey-retry-button">
                          <KeyRound className="h-4 w-4 mr-1.5" /> Retry security key
                        </Button>
                        <Button type="button" variant="outline"
                          onClick={() => setShowFallback(true)}
                          className="h-10 rounded-sm"
                          data-testid="login-yubikey-fallback-toggle">
                          <ShieldAlert className="h-4 w-4 mr-1.5" /> Use master fallback
                        </Button>
                      </div>
                    </>
                  ) : (
                    <form onSubmit={handleFallback} className="space-y-3" data-testid="login-yubikey-fallback-form">
                      <Label htmlFor="fallback-pwd" className="label-overline">Master fallback password</Label>
                      <Input id="fallback-pwd" type="password" required autoFocus
                        value={fallbackPwd} onChange={(e) => setFallbackPwd(e.target.value)}
                        placeholder="•••••••••••••••"
                        className="rounded-sm h-11 font-mono"
                        data-testid="login-yubikey-fallback-input" />
                      <Button type="submit" disabled={busy || !fallbackPwd}
                        className="w-full h-11 rounded-sm bg-[#002FA7] hover:bg-[#00227A] text-white font-semibold"
                        data-testid="login-yubikey-fallback-submit">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in with fallback"}
                      </Button>
                      <button type="button"
                        className="text-xs label-overline text-zinc-500 hover:text-zinc-900"
                        onClick={() => setShowFallback(false)}
                        data-testid="login-yubikey-back-to-key">
                        ← Try security key again
                      </button>
                    </form>
                  )}
                  <button type="button"
                    className="text-xs label-overline text-zinc-500 hover:text-zinc-900"
                    onClick={resetLogin}
                    data-testid="login-yubikey-restart-button">
                    ← Start over
                  </button>
                </div>
              ) : twofa ? (
                <form onSubmit={handleVerify} className="space-y-4" data-testid="login-2fa-form">
                  <div>
                    <div className="label-overline mb-1">Two-factor verification</div>
                    <p className="text-sm text-zinc-500">
                      Enter the 6-digit code we sent to
                      {twofa.phone_hint && twofa.sms_sent
                        ? <> your phone <strong>{twofa.phone_hint}</strong></>
                        : null}
                      {twofa.phone_hint && twofa.sms_sent && twofa.email_hint && twofa.email_sent ? " and " : ""}
                      {twofa.email_hint && twofa.email_sent
                        ? <> your email <strong>{twofa.email_hint}</strong></>
                        : null}
                      {!(twofa.sms_sent || twofa.email_sent)
                        ? " your phone and email"
                        : null}.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="2facode" className="label-overline">Verification code</Label>
                    <Input id="2facode" type="text" inputMode="numeric" autoFocus
                      maxLength={6} pattern="[0-9]{6}" required
                      value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                      placeholder="123456" className="rounded-sm h-11 text-center font-mono text-lg tracking-[0.4em]"
                      data-testid="login-2fa-code-input" />
                  </div>
                  <Button type="submit" disabled={busy || code.length < 6}
                    className="w-full h-11 rounded-sm bg-[#002FA7] hover:bg-[#00227A] text-white font-semibold"
                    data-testid="login-2fa-verify-button">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & sign in"}
                  </Button>
                  <button type="button"
                    className="text-xs label-overline text-zinc-500 hover:text-zinc-900"
                    onClick={() => { setTwofa(null); setCode(""); }}
                    data-testid="login-2fa-cancel-button">
                    ← Back to sign-in
                  </button>
                </form>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="label-overline">Email</Label>
                    <Input id="email" type="email" required autoComplete="email"
                      value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@company.com" className="rounded-sm h-11"
                      data-testid="login-email-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password" className="label-overline">Password</Label>
                    <Input id="password" type="password" required autoComplete="current-password"
                      value={password} onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••" className="rounded-sm h-11"
                      data-testid="login-password-input" />
                  </div>
                  <Button type="submit" disabled={busy}
                    className="w-full h-11 rounded-sm bg-[#002FA7] hover:bg-[#00227A] text-white font-semibold"
                    data-testid="login-submit-button">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
                  </Button>
                </form>
              )}
            </TabsContent>

            <TabsContent value="google" className="mt-6 space-y-4">
              <Button onClick={handleGoogle}
                className="w-full h-11 rounded-sm bg-white border border-zinc-200 text-zinc-900 hover:bg-zinc-100"
                data-testid="google-login-button">
                <svg className="h-4 w-4 mr-2" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.5 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.4 6 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.5-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.4 6 29.5 4 24 4 16.3 4 9.6 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.4 0 10.2-2.1 13.9-5.5l-6.4-5.4C29.4 35 26.8 36 24 36c-5.2 0-9.6-3.5-11.2-8.2l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.2 5.6l6.4 5.4C40.3 36.1 44 30.6 44 24c0-1.3-.1-2.5-.4-3.5z"/></svg>
                Continue with Google
              </Button>
              <p className="text-xs text-zinc-500 leading-relaxed">
                Google sign-in is for client users. Your account is auto-created on first sign-in.
                Admins manage which Client company you belong to.
              </p>
            </TabsContent>
          </Tabs>

          <div className="flex items-center gap-3 text-[11px] text-zinc-400 pt-2 flex-wrap">
            <Link to="/terms" className="hover:text-[#002FA7]" data-testid="login-terms-link">
              Terms of Service
            </Link>
            <span>·</span>
            <Link to="/privacy" className="hover:text-[#002FA7]" data-testid="login-privacy-link">
              Privacy Policy
            </Link>
            <span className="ml-auto">&copy; {new Date().getFullYear()} Iceberg Digital</span>
          </div>
        </div>
      </div>
    </div>
  );
}
